// Brevo (Sendinblue) client tracker + server-side activity sync.
//
// - `identifyBrevo` / `trackBrevo` use Brevo's Marketing Automation JS tracker
//   (sa.js). The MA key is publishable — safe to ship in the client bundle.
// - `touchActivity()` fires our own edge function which updates
//   LAST_ACTIVE_AT and (optionally) increments IMAGE_COUNT_TOTAL via the
//   Brevo REST API (INCREMENT operator). SIGNUP_DATE is never sent from here.

import { supabase } from "@/integrations/supabase/client";

const MA_KEY = (import.meta.env.VITE_BREVO_MA_KEY as string | undefined)?.trim();

type BrevoWindow = Window & {
  sendinblue?: {
    identify: (email: string, attrs?: Record<string, unknown>) => void;
    track: (event: string, props?: Record<string, unknown>, opts?: unknown) => void;
    page?: (name?: string, props?: Record<string, unknown>) => void;
  };
  sib?: { client_key?: string };
};

let loaded = false;
let loadPromise: Promise<void> | null = null;

function loadTracker(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (!MA_KEY) return Promise.resolve();
  if (loaded) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve) => {
    try {
      const w = window as BrevoWindow;
      w.sib = { client_key: MA_KEY };
      const s = document.createElement("script");
      s.async = true;
      s.src = "https://sibautomation.com/sa.js?key=" + encodeURIComponent(MA_KEY);
      s.onload = () => {
        loaded = true;
        resolve();
      };
      s.onerror = () => resolve(); // swallow — analytics must never break the app
      document.head.appendChild(s);
    } catch {
      resolve();
    }
  });
  return loadPromise;
}

export async function identifyBrevo(params: {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  createdAt?: string | null;
}): Promise<void> {
  if (!MA_KEY || !params.email) return;
  await loadTracker();
  try {
    const attrs: Record<string, unknown> = {};
    if (params.firstName) attrs.FIRSTNAME = params.firstName;
    if (params.lastName) attrs.LASTNAME = params.lastName;
    // Client-side fallback: DB trigger sets SIGNUP_DATE via pg_net, but if
    // the vault secret is missing the trigger silently skips. Send it here
    // too — Brevo ignores duplicate writes to the same value.
    if (params.createdAt) attrs.SIGNUP_DATE = params.createdAt;
    (window as BrevoWindow).sendinblue?.identify(params.email, attrs);
  } catch (err) {
    console.warn("[brevo] identify failed", err);
  }
}

export async function trackBrevo(
  event: string,
  properties?: Record<string, unknown>,
): Promise<void> {
  // Route through the edge function so we don't need the MA client key.
  void supabase.functions
    .invoke("sync-brevo-contact", {
      body: { event: "track", name: event, properties: properties ?? {} },
    })
    .catch((err) => console.warn("[brevo] track failed", err));
}

// --- Server-side activity sync (LAST_ACTIVE_AT + IMAGE_COUNT_TOTAL) ---

const MIN_ACTIVITY_INTERVAL_MS = 30_000;
let lastActivityAt = 0;

/**
 * Fire-and-forget: refresh LAST_ACTIVE_AT (and optionally increment
 * IMAGE_COUNT_TOTAL) on the user's Brevo contact. Debounced per session to
 * 30s unless `incrementImageCount` is true (image counts always sync
 * immediately so the running total stays accurate).
 */
export function touchActivity(opts?: { incrementImageCount?: boolean }): void {
  const increment = !!opts?.incrementImageCount;
  const now = Date.now();
  if (!increment && now - lastActivityAt < MIN_ACTIVITY_INTERVAL_MS) return;
  lastActivityAt = now;

  void supabase.functions
    .invoke("sync-brevo-contact", {
      body: { event: "activity", incrementImageCount: increment },
    })
    .catch((err) => console.warn("[brevo] sync-activity failed", err));
}

// Note: signup contact upsert (SIGNUP_DATE, FIRSTNAME, LASTNAME,
// MARKETING_OPT_IN) is handled server-side by an AFTER INSERT trigger on
// public.profiles, which calls sync-brevo-contact via pg_net. That path is
// resilient to email-confirmation / OAuth redirects tearing down the tab.

